import { TablePaginationConfig, TableProps } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { PaginatedParams } from '../core/http/pagination';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useDebounce from './useDebounce';

export interface TableChangeParams {
    pagination: TablePaginationConfig;
    filters?: Record<string, FilterValue | null>;
    sorter?: SorterResult<any> | SorterResult<any>[];
}

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

const createSearchParamName = (tableName: string | undefined, param: string) => {
    return tableName ? `${tableName}.${param}` : param;
};

const createSearchParamNames = (tableName: string | undefined) => {
    return {
        page: createSearchParamName(tableName, 'page'),
        size: createSearchParamName(tableName, 'size'),
        sort: createSearchParamName(tableName, 'sort'),
        sortDirection: createSearchParamName(tableName, 'sortDirection'),
        query: createSearchParamName(tableName, 'query'),
    };
};

export const useHandleTableChange = <TFilterKeys extends readonly string[]>(args: {
    tableName?: string;
    filterKeys?: TFilterKeys;
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState<string | null>(null);
    const [debouncedQuery, setDebouncedQuery] = useState<string | null>(null);
    const [paginator, setPaginator] = useState<Required<Pick<TablePaginationConfig, 'pageSize' | 'current' | 'total'>>>(
        {
            pageSize: DEFAULT_SIZE,
            current: DEFAULT_PAGE,
            total: 0,
        }
    );
    const [filters, setFilters] = useState<Record<TFilterKeys[number], any> | null>(null);
    const [sort, setSort] = useState<{
        sort: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sort: 'CREATED_AT',
        sortDirection: 'DESC',
    });

    const SEARCH_PARAM_NAME = createSearchParamNames(args.tableName);

    // Setting the state based on the search params
    useEffect(() => {
        if (searchParams.get(SEARCH_PARAM_NAME.page)) {
            setPaginator((prev) => ({ ...prev, current: Number(searchParams.get(SEARCH_PARAM_NAME.page)) }));
        }
        if (searchParams.get(SEARCH_PARAM_NAME.size)) {
            setPaginator((prev) => ({ ...prev, pageSize: Number(searchParams.get(SEARCH_PARAM_NAME.size)) }));
        }
        if (searchParams.get(SEARCH_PARAM_NAME.query)) {
            setQuery(searchParams.get(SEARCH_PARAM_NAME.query) ?? null);
        }
        if (searchParams.get(SEARCH_PARAM_NAME.sortDirection)) {
            const sortDirection = searchParams.get(SEARCH_PARAM_NAME.sortDirection);
            if (sortDirection === 'ASC' || sortDirection === 'DESC') {
                setSort((prev) => ({ ...prev, sortDirection }));
            } else {
                setSort((prev) => ({ ...prev, sortDirection: 'DESC' }));
            }
        }
        if (searchParams.get(SEARCH_PARAM_NAME.sort)) {
            setSort((prev) => ({ ...prev, sort: searchParams.get(SEARCH_PARAM_NAME.sort) ?? 'CREATED_AT' }));
        }
        if (args.filterKeys) {
            let filters: Record<string, any> = {};
            args.filterKeys.forEach((key) => {
                const filterSearchParamName = createSearchParamName(args.tableName, key);
                if (searchParams.get(filterSearchParamName)) {
                    const value = searchParams.get(filterSearchParamName);
                    if (value?.includes(',')) {
                        filters[key] = value.split(',');
                    } else {
                        filters[key] = value;
                    }
                }
            });
            setFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        setSearchParams(
            (() => {
                searchParams.set(SEARCH_PARAM_NAME.sort, sort.sort);
                searchParams.set(SEARCH_PARAM_NAME.sortDirection, sort.sortDirection);
                return searchParams;
            })(),
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onTableChange = (pagination: TablePaginationConfig) => {
        setPaginator({
            ...pagination,
            total: pagination.total ?? 0,
            current: pagination.current ?? DEFAULT_PAGE,
            pageSize: pagination.pageSize ?? DEFAULT_SIZE,
        });
        setSearchParams(
            (() => {
                searchParams.set(SEARCH_PARAM_NAME.page, (pagination.current ?? DEFAULT_PAGE).toString());
                searchParams.set(SEARCH_PARAM_NAME.size, (pagination.pageSize ?? DEFAULT_SIZE).toString());
                return searchParams;
            })(),
            { replace: true }
        );
    };

    const onFilters = (filters: Record<TFilterKeys[number], any> | null) => {
        setFilters(filters);
        setSearchParams(
            (() => {
                if (!args?.filterKeys) return searchParams;
                for (const key of args.filterKeys) {
                    const value = filters?.[key as TFilterKeys[number]];
                    const filterSearchParamName = createSearchParamName(args.tableName, key);
                    if (!value) {
                        searchParams.delete(filterSearchParamName);
                    } else {
                        if (Array.isArray(value)) {
                            if (value.length === 0) {
                                searchParams.delete(filterSearchParamName);
                            } else {
                                searchParams.set(filterSearchParamName, value.join(','));
                            }
                        } else {
                            searchParams.set(filterSearchParamName, value.toString());
                        }
                    }
                }
                return searchParams;
            })(),
            { replace: true }
        );
    };

    const debounceSearch = useDebounce((query: string | null, searchParams: URLSearchParams) => {
        if (query) {
            setSearchParams(
                (() => {
                    searchParams.set(SEARCH_PARAM_NAME.query, query);
                    searchParams.set(SEARCH_PARAM_NAME.page, DEFAULT_PAGE.toString());
                    searchParams.set(SEARCH_PARAM_NAME.size, DEFAULT_SIZE.toString());
                    return searchParams;
                })(),
                { replace: true }
            );
            setDebouncedQuery(query);
        } else {
            setSearchParams(
                (() => {
                    searchParams.delete(SEARCH_PARAM_NAME.query);
                    searchParams.set(SEARCH_PARAM_NAME.page, DEFAULT_PAGE.toString());
                    searchParams.set(SEARCH_PARAM_NAME.size, DEFAULT_SIZE.toString());
                    return searchParams;
                })(),
                { replace: true }
            );
            setDebouncedQuery(null);
        }
    }, 500);

    const onSearch = (query: string | null) => {
        setQuery(query);
        debounceSearch(query, searchParams);
    };

    // Not used for now
    // const getArgs = <T extends Record<string, any> = {}>(appendArgs?: T) => {
    //     return {
    //         page: (searchParams.get('page') ? Number(searchParams.get('page')) : DEFAULT_PAGE) - 1,
    //         size: searchParams.get('size') ? Number(searchParams.get('size')) : DEFAULT_SIZE,
    //         sort: searchParams.get('sort') ? searchParams.get('sort') : 'CREATED_AT',
    //         sortDirection: searchParams.get('sortDirection') ? searchParams.get('sortDirection') : 'DESC',
    //         ...(args?.filterKeys
    //             ? args.filterKeys.reduce((acc: Record<string, any>, key: string) => {
    //                   acc[key] = searchParams.get(key);
    //                   return acc;
    //               }, {})
    //             : {}),
    //         ...(searchParams.get('query') ? { query: searchParams.get('query') } : {}),
    //         ...appendArgs,
    //     };
    // };

    const clearFilter = (key: TFilterKeys[number], index?: number) => {
        if (!filters || Object.keys(filters).length === 0) return;

        const newFilters = { ...filters };
        if (index !== undefined) {
            newFilters[key].splice(index, 1);
        } else {
            delete newFilters[key];
        }
        onFilters(newFilters);
    };

    const setFilter = (key: TFilterKeys[number], value: any) => {
        if (!value) {
            clearFilter(key);
        } else {
            onFilters({ ...(filters ?? ({} as Record<TFilterKeys[number], any>)), [key]: value });
        }
    };

    const clearAllFilters = () => {
        onFilters(null);
    };

    return {
        page: paginator.current - 1,
        pageSize: paginator.pageSize,
        onTableChange,
        onSearch,
        query: {
            current: query,
            debounced: debouncedQuery,
        },
        searchParams,
        sort,
        setFilter,
        filters,
        clearFilter,
        clearAllFilters,
    };
};

export const getTableChangeParams = ({ pagination, sorter }: TableChangeParams) => {
    const params: Partial<PaginatedParams> = {};

    if (pagination && pagination.current) {
        params.page = pagination.current - 1;
        params.size = pagination.pageSize;
    }

    if (sorter && !Array.isArray(sorter)) {
        let sortDirection: 'ASC' | 'DESC' | undefined = undefined;
        if (sorter.order === 'ascend') sortDirection = 'ASC';
        else if (sorter.order === 'descend') sortDirection = 'DESC';

        params.sortDirection = sortDirection;
        params.sortParameter = sorter.columnKey?.toString();
    }

    return params;
};

export enum SortDirections {
    ASC = 'ASC',
    DESC = 'DESC',
}

export enum AntdSortOrder {
    ASCEND = 'ascend',
    DESCEND = 'descend',
}

export type SortOrder = 'ascend' | 'descend' | null;
export type SortDirection = 'ASC' | 'DESC' | undefined;
export type TableChangeHandler<T> = NonNullable<TableProps<T>['onChange']>;

export const convertAntdOrderToApiDirection = (order: string | undefined): SortDirection => {
    switch (order) {
        case AntdSortOrder.ASCEND:
            return SortDirections.ASC;
        case AntdSortOrder.DESCEND:
            return SortDirections.DESC;
        default:
            return undefined;
    }
};

export const toAntOrder = (dir?: 'ASC' | 'DESC') => (dir === 'ASC' ? 'ascend' : dir === 'DESC' ? 'descend' : null);
